import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ACCOUNT_STATUS, PLANS } from "@/lib/constants";
import { Upload, ImageIcon, Loader2 } from "lucide-react";
import { EmailNotificationsCard } from "@/components/settings/EmailNotificationsCard";

const COLOR_PRESETS = [
  "#0F172A", "#1E40AF", "#0F766E", "#B91C1C", "#7C2D12", "#4338CA",
];

export default function Configuracion() {
  const { session, hasPermission } = useAuth();
  const { activeTenant, refreshTenant, isSuperAdmin } = useTenant();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (activeTenant) setForm({ ...activeTenant });
  }, [activeTenant]);

  if (!activeTenant || !form) {
    return (
      <div className="space-y-6" data-testid="page-configuracion">
        <PageHeader
          title="Configuración"
          description="Datos del tenant, branding y contacto."
        />
        <Card className="border-border">
          <CardContent className="p-8 text-sm text-muted-foreground">
            {isSuperAdmin
              ? "Selecciona un tenant en la sección de Tenants para editar su configuración."
              : "Cargando configuración del tenant…"}
          </CardContent>
        </Card>
      </div>
    );
  }

  const canEdit = isSuperAdmin || session?.user?.role_name === "dueno";

  async function onSave(e) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    try {
      await api.patch(`/tenants/${form.id}`, {
        commercial_name: form.commercial_name,
        legal_name: form.legal_name,
        rfc: form.rfc,
        email: form.email,
        phone: form.phone,
        address: form.address,
        primary_color: form.primary_color,
      });
      if (isSuperAdmin) {
        await api.patch(`/admin/tenants/${form.id}/commercial`, {
          account_status: form.account_status,
          plan_name: form.plan_name,
        });
      }
      toast.success("Configuración guardada");
      refreshTenant();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function onUploadLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/tenants/${form.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo actualizado");
      setForm({ ...form, logo_url: data.logo_url });
      refreshTenant();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  const logoSrc = form.logo_url
    ? form.logo_url.startsWith("http")
      ? form.logo_url
      : `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`
    : null;

  return (
    <div className="space-y-6" data-testid="page-configuracion">
      <PageHeader
        title="Configuración del tenant"
        description="Estos datos son visibles en el header, en el login del cliente y en documentos futuros (órdenes, cotizaciones)."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Branding card */}
        <Card className="border-border lg:col-span-1">
          <CardHeader>
            <CardTitle className="tracking-tight">Branding</CardTitle>
            <CardDescription>Logo y color primario del tenant.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Logo</div>
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-lg border border-border bg-slate-50 flex items-center justify-center overflow-hidden">
                  {logoSrc ? (
                    <img src={logoSrc} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="config-logo-preview" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-slate-300" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={onUploadLogo}
                    data-testid="config-logo-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={!canEdit || uploading}
                    data-testid="config-logo-upload"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    {uploading ? "Subiendo…" : "Subir logo"}
                  </Button>
                  <p className="text-[11px] text-muted-foreground">PNG, JPG o WebP · máx 5 MB</p>
                </div>
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Color primario</div>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  disabled={!canEdit}
                  className="h-10 w-14 rounded border border-border cursor-pointer"
                  data-testid="config-color-picker"
                />
                <Input
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  disabled={!canEdit}
                  className="mono uppercase"
                  data-testid="config-color-input"
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => setForm({ ...form, primary_color: c })}
                    className="h-7 w-7 rounded-md border border-border"
                    style={{ background: c }}
                    data-testid={`color-preset-${c.replace("#", "")}`}
                    aria-label={`Preset ${c}`}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data card */}
        <Card className="border-border lg:col-span-2">
          <CardHeader className="border-b border-border">
            <CardTitle className="tracking-tight">Datos generales</CardTitle>
            <CardDescription>Se usarán en documentos y encabezados.</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={onSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre comercial *</Label>
                  <Input
                    required
                    value={form.commercial_name || ""}
                    onChange={(e) => setForm({ ...form, commercial_name: e.target.value })}
                    disabled={!canEdit}
                    data-testid="config-commercial-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Razón social</Label>
                  <Input
                    value={form.legal_name || ""}
                    onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                    disabled={!canEdit}
                    data-testid="config-legal-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>RFC</Label>
                  <Input
                    value={form.rfc || ""}
                    onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                    disabled={!canEdit}
                    className="mono uppercase"
                    data-testid="config-rfc"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={form.phone || ""}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    disabled={!canEdit}
                    data-testid="config-phone"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    disabled={!canEdit}
                    data-testid="config-email"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Dirección</Label>
                  <Textarea
                    rows={2}
                    value={form.address || ""}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    disabled={!canEdit}
                    data-testid="config-address"
                  />
                </div>
                {isSuperAdmin && (
                  <>
                    <div className="space-y-2">
                      <Label>Estatus de cuenta</Label>
                      <Select value={form.account_status} onValueChange={(v) => setForm({ ...form, account_status: v })}>
                        <SelectTrigger data-testid="config-account-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ACCOUNT_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Plan</Label>
                      <Select value={form.plan_name} onValueChange={(v) => setForm({ ...form, plan_name: v })}>
                        <SelectTrigger data-testid="config-plan"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PLANS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end pt-4">
                <Button type="submit" disabled={saving || !canEdit} className="btn-tenant" data-testid="config-save">
                  {saving ? "Guardando…" : "Guardar cambios"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      {hasPermission("settings:view") && <EmailNotificationsCard />}
    </div>
  );
}
